import React, { useState, useEffect } from "react";
import {
  FaListOl,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaBuilding,
  FaMapMarkerAlt,
  FaBarcode,
  FaCheckCircle,
  FaKey,
  FaLock,
  FaFingerprint,
} from "react-icons/fa";
import apiRequest from "../services/api";

const BranchManagement = () => {
  const [branches, setBranches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Modal Sheet States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Form State (branch + all etax/EIMS fields)
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    phone: "",
    location: "",
    status: "ACTIVE",
    etaxClientId: "",
    etaxClientSecret: "",
    etaxApiKey: "",
    systemNumber: "",
    eimsPrivateKey: "",
    eimsCertificate: "",
    sequences: [],
    previousIrn: "",
  });

  const handleSequenceChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.sequences];
      updated[index] = {
        ...updated[index],
        [field]: field === "currentValue" ? Number(value) || 0 : value,
      };
      return { ...prev, sequences: updated };
    });
  };

  const addSequence = () => {
    setFormData((prev) => ({
      ...prev,
      sequences: [
        ...prev.sequences,
        { sequenceName: "", currentValue: 0, isExisting: false },
      ],
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const fetchBranches = async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("settings/branches");
      setBranches(resp.data || []);
    } catch (err) {
      setError(err?.message || "Failed to synchronize system branch data.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const openModal = (mode, branch = null) => {
    setModalMode(mode);
    setSelectedBranch(branch);
    setError("");

    if (branch) {
      setFormData({
        id: branch.id ?? branch.branchId,
        name: branch.name || "",
        phone: branch.phone || "",
        location: branch.location || "",
        status: branch.status || "ACTIVE",
        etaxClientId: branch.etaxClientId || "",
        etaxClientSecret: branch.etaxClientSecret || "",
        etaxApiKey: branch.etaxApiKey || "",
        systemNumber: branch.systemNumber || "",
        eimsPrivateKey: branch.eimsPrivateKey || "",
        eimsCertificate: branch.eimsCertificate || "",
        previousIrn: branch.previousIrn || "",
        sequences: Array.isArray(branch.sequences)
          ? branch.sequences.map((s) => ({
              sequenceName: s.sequenceName || "",
              currentValue: s.currentValue ?? 0,
              isExisting: true,
            }))
          : [],
      });
    } else {
      setFormData({
        id: null,
        name: "",
        phone: "",
        location: "",
        status: "ACTIVE",
        etaxClientId: "",
        etaxClientSecret: "",
        etaxApiKey: "",
        systemNumber: "",
        eimsPrivateKey: "",
        eimsCertificate: "",
        previousIrn: "",
        sequences: [],
      });
    }

    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    const endpoint = "settings/branches";
    const method = modalMode === "create" ? "POST" : "PUT";
    for (const seq of formData.sequences) {
      if (!seq.sequenceName?.trim()) {
        setError("All sequences must have a name.");
        return;
      }
    }
    const payload = {
      ...formData,
      sequences: formData.sequences.map((s) => ({
        sequenceName: s.sequenceName.trim(),
        currentValue: Number(s.currentValue) || 0,
      })),
    };

    try {
      await apiRequest(endpoint, {
        method,
        body: payload,
      });

      fetchBranches();
      setIsModalOpen(false);
    } catch (err) {
      setError(
        err?.message ||
          "Persistence engine rejected the branch payload context."
      );
    }
  };

  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you certain you want to change status to DELETED for branch "${name}"?`
      )
    )
      return;

    try {
      await apiRequest("settings/branches", {
        method: "PUT",
        body: { id, status: "DELETED" },
      });

      fetchBranches();
    } catch (err) {
      setError(err?.message || "Branch deactivation sequence failed.");
    }
  };

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const textareaClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50 font-mono text-[11px] resize-y";

  const labelClass =
    "text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1";

  const selectClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 disabled:opacity-60 transition appearance-none dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50";
  const sectionClass =
    "bg-white border border-slate-200 rounded-2xl p-6 space-y-5 shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24">
      {/* Module Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <FaBuilding className="text-emerald-500" />
            Branch
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Configure business branches and eTax credentials
          </p>
        </div>

        <button
          onClick={() => openModal("create")}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
        >
          <FaPlus />
          New Branch
        </button>
      </div>

      {/* Exception Banner */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Branches Table */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                <th className="px-6 py-4">Branch Name</th>
                <th className="px-6 py-4">Telephone</th>
                <th className="px-6 py-4">Geographic Location</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-8 text-slate-400 dark:text-slate-500"
                  >
                    Querying business branches...
                  </td>
                </tr>
              ) : branches.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-8 text-slate-400 dark:text-slate-500"
                  >
                    No branch found.
                  </td>
                </tr>
              ) : (
                branches.map((b) => (
                  <tr
                    key={b.id ?? b.branchId}
                    className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-6 py-4 font-semibold">
                      <span className="text-sm font-bold tracking-wide text-slate-800 dark:text-slate-200 flex items-center gap-2">
                        <FaBuilding className="text-slate-400 dark:text-slate-500 text-xs" />
                        {b.name}
                      </span>
                    </td>

                    <td className="px-6 py-4 font-mono text-xs text-blue-600 tracking-wider dark:text-blue-400">
                      {b.phone}
                    </td>

                    <td className="px-6 py-4 text-slate-500 dark:text-slate-400">
                      <span className="flex items-center gap-1.5">
                        <FaMapMarkerAlt className="text-[10px] text-slate-400 dark:text-slate-500" />
                        {b.location || "Not Provided"}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${
                          b.status === "INACTIVE" || b.status === "DELETED"
                            ? "text-red-600 dark:text-red-400"
                            : "text-emerald-600 dark:text-emerald-400"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            b.status === "INACTIVE" || b.status === "DELETED"
                              ? "bg-red-500 dark:bg-red-400"
                              : "bg-emerald-500 dark:bg-emerald-400"
                          } animate-pulse`}
                        />
                        {b.status || "ACTIVE"}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openModal("view", b)}
                          className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
                        >
                          <FaEye />
                        </button>

                        <button
                          onClick={() => openModal("edit", b)}
                          className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-200 transition dark:bg-blue-600/10 dark:hover:bg-blue-600/20 dark:text-blue-400 dark:border-blue-500/10"
                        >
                          <FaEdit />
                        </button>

                        <button
                          onClick={() =>
                            handleDelete(b.id ?? b.branchId, b.name)
                          }
                          className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-200 transition dark:bg-red-600/10 dark:hover:bg-red-600/20 dark:text-red-400 dark:border-red-500/10"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Modal Sheet */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70" />

        {/* Drawer */}
        <div
          className={`relative w-full max-w-2xl mx-auto bg-white border border-slate-200 rounded-t-3xl p-6 sm:p-8 shadow-[0_-15px_40px_rgba(15,23,42,0.15)] dark:bg-slate-900 dark:border-white/10 dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)] transition-transform duration-300 transform space-y-6 overflow-y-auto max-h-[85vh] ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                {modalMode} Branch
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Configure branch identity, contact, location and eTax
                credentials.
              </p>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition-colors dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Branch Name */}
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>
                  <FaBuilding className="text-slate-400 dark:text-slate-500 text-[10px]" />
                  Legal Branch Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Bole Hub"
                  disabled={modalMode === "view"}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              {/* Telephone */}
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>
                  <FaBarcode className="text-slate-400 dark:text-slate-500 text-[10px]" />
                  Telephone
                </label>
                <input
                  type="text"
                  required
                  placeholder=""
                  disabled={modalMode === "view"}
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      phone: e.target.value.toUpperCase(),
                    })
                  }
                  className={`${inputClass} uppercase`}
                />
              </div>

              {/* Location */}
              <div className="flex flex-col gap-1.5 sm:col-span-2">
                <label className={labelClass}>
                  <FaMapMarkerAlt className="text-slate-400 dark:text-slate-500 text-[10px]" />
                  Branch Location
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Addis Ababa, Ethiopia"
                  disabled={modalMode === "view"}
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              {/* Status */}
              {modalMode !== "create" && (
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label className={labelClass}>
                    <FaCheckCircle className="text-slate-400 dark:text-slate-500 text-[10px]" />
                    State Toggle
                  </label>
                  <select
                    disabled={modalMode === "view"}
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({ ...formData, status: e.target.value })
                    }
                    className={selectClass}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">SUSPENDED</option>
                  </select>
                </div>
              )}
            </div>

            {/* ===================== ETAX / EIMS CREDENTIALS ===================== */}
            <div className="pt-4 border-t border-slate-200 dark:border-white/10 space-y-4">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-2">
                <FaKey className="text-amber-500 text-[11px]" />
                EIMS / eTax Credentials
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Client ID */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    <FaKey className="text-slate-400 dark:text-slate-500 text-[10px]" />
                    EIMS Client ID
                  </label>
                  <input
                    type="text"
                    placeholder="Client ID"
                    disabled={modalMode === "view"}
                    value={formData.etaxClientId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        etaxClientId: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                {/* Client Secret */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    <FaLock className="text-slate-400 dark:text-slate-500 text-[10px]" />
                    EIMS Client Secret
                  </label>
                  <input
                    type="password"
                    placeholder="Client Secret"
                    disabled={modalMode === "view"}
                    value={formData.etaxClientSecret}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        etaxClientSecret: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                {/* API Key */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    <FaKey className="text-slate-400 dark:text-slate-500 text-[10px]" />
                    EIMS API Key
                  </label>
                  <input
                    type="text"
                    placeholder="API Key"
                    disabled={modalMode === "view"}
                    value={formData.etaxApiKey}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        etaxApiKey: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                {/* System Number */}
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    <FaFingerprint className="text-slate-400 dark:text-slate-500 text-[10px]" />
                    System Number
                  </label>
                  <input
                    type="text"
                    placeholder="System Number"
                    disabled={modalMode === "view"}
                    value={formData.systemNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        systemNumber: e.target.value,
                      })
                    }
                    className={inputClass}
                  />
                </div>

                {/* Private Key */}
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label className={labelClass}>
                    <FaKey className="text-slate-400 dark:text-slate-500 text-[10px]" />
                    EIMS Private Key
                  </label>
                  <textarea
                    rows={5}
                    placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                    disabled={modalMode === "view"}
                    value={formData.eimsPrivateKey}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        eimsPrivateKey: e.target.value,
                      })
                    }
                    className={textareaClass}
                  />
                </div>

                {/* Certificate */}
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <label className={labelClass}>
                    <FaFingerprint className="text-slate-400 dark:text-slate-500 text-[10px]" />
                    EIMS Certificate
                  </label>
                  <textarea
                    rows={5}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                    disabled={modalMode === "view"}
                    value={formData.eimsCertificate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        eimsCertificate: e.target.value,
                      })
                    }
                    className={textareaClass}
                  />
                </div>
              </div>
            </div>

            {/* ===================== SEQUENCES ===================== */}
            <div className={sectionClass}>
              <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3">
                <h2 className="text-sm font-bold text-slate-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <FaListOl className="text-indigo-600 dark:text-indigo-400 text-xs" />
                  Document Sequences
                </h2>
                <button
                  type="button"
                  onClick={addSequence}
                  className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                >
                  <FaPlus className="text-[10px]" />
                  Add Sequence
                </button>
              </div>

              {formData.sequences.length === 0 ? (
                <p className="text-xs text-slate-400 dark:text-slate-500 py-4 text-center">
                  No sequences configured yet.
                </p>
              ) : (
                <div className="space-y-3">
                  {formData.sequences.map((seq, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end bg-slate-50 dark:bg-slate-950/40 p-3 rounded-xl border border-slate-200 dark:border-white/10"
                    >
                      <div className="sm:col-span-7">
                        <label className={labelClass}>Sequence Name</label>
                        <select
                          value={seq.sequenceName}
                          onChange={(e) =>
                            handleSequenceChange(
                              index,
                              "sequenceName",
                              e.target.value
                            )
                          }
                          disabled={seq.isExisting}
                          className={`w-full border rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500
      ${
        seq.isExisting
          ? "bg-slate-100 text-slate-500 cursor-not-allowed dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-white/10"
          : "bg-white text-slate-800 border-slate-200 dark:bg-slate-900 dark:text-white dark:border-white/10"
      }`}
                        >
                          <option value="">Select Sequence Name</option>
                          {[
                            "DOCUMENT_NUMBER",
                            "INVOICE_COUNTER",
                            "RECEIPT_COUNTER",
                            "WITHHOLDING_RECEIPT_COUNTER",
                            "CASH_SALE_COUNTER",
                            "CREDIT_SALE_COUNTER",
                            "CASH_PURCHASE_COUNTER",
                            "CREDIT_PURCHASE_COUNTER",
                            "IN_PAYMENT_COUNTER",
                            "OUT_PAYMENT_COUNTER",
                            "PURCHASE_RETURN_COUNTER",
                            "SALE_RETURN_COUNTER",
                            "INVENTORY_ACTION_COUNTER",
                            "LETTER_COUNTER",
                          ].map((name) => (
                            <option key={name} value={name}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="sm:col-span-5">
                        <label className={labelClass}>Current Value</label>
                        <input
                          type="number"
                          min="0"
                          value={seq.currentValue}
                          onChange={(e) =>
                            handleSequenceChange(
                              index,
                              "currentValue",
                              e.target.value
                            )
                          }
                          className="w-full bg-white border border-slate-200 text-slate-800 rounded-lg px-3 py-2 text-xs font-mono text-right focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-900 dark:border-white/10 dark:text-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-2">
                Existing sequence names cannot be changed. You can only update
                the current value or add new sequences.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Last Sales IRN</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 dark:text-slate-500">
                  <FaKey className="text-xs" />
                </span>
                <input
                  type="text"
                  name="previousIrn"
                  value={formData.previousIrn}
                  onChange={handleInputChange}
                  placeholder="Last IRN"
                  className={inputClass}
                />
              </div>
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                Close
              </button>

              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                >
                  {modalMode === "create"
                    ? "Save Branch"
                    : "Apply Modifications"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BranchManagement;
